import type { DishAllergen } from "api-types";

/**
 * Every string the choice-group surfaces say, in one place. The staff app has
 * no i18n, so these are literals, but they are literals a reviewer can read end
 * to end. That matters more than usual here: the difference between "can't be
 * read" and saying nothing is the difference between a waiter asking the
 * kitchen and a waiter answering from an incomplete screen.
 *
 * One state, one phrasing. "Confirmed" already means "known present" on the
 * allergen chips, so anything we could not parse says "can't be read"
 * everywhere rather than borrowing that word for a second meaning.
 */
const choicesLabel = (count: number): string =>
  count === 1 ? "1 choice" : `${count} choices`;

export const staffMenuCopy = {
  choices: choicesLabel,

  close: "Close",
  calories: (kcal: number): string => `${kcal} kcal`,

  unreadable: (count: number): string =>
    count === 1 ? "1 can’t be read" : `${count} can’t be read`,

  /**
   * A group's facts: whether it must be chosen from, and what it offers.
   *
   * A group holding only other groups offers nothing to pick at this level, so
   * it states the picks it asks for instead of "0 options".
   */
  groupMeta: (required: boolean, options: number, nestedGroups = 0): string => {
    const parts = [required ? "Required" : "Optional"];
    if (options > 0) {
      parts.push(`${options} ${options === 1 ? "option" : "options"}`);
    }
    if (nestedGroups > 0) parts.push(choicesLabel(nestedGroups));
    if (parts.length === 1) parts.push("0 options");
    return parts.join(" · ");
  },

  bandsHint: "Tick an allergen to see which options are suitable.",
  /** The same control on a dish with no choices to mark up. */
  bandsHintNoGroups: "Tick an allergen to find it in the list below.",
  /** Section headings, in the order the drawer states them. */
  sectionRecipeInfo: "Recipe info",
  sectionDiet: "Diet",
  sectionChoices: "Choices",
  /** Restates the question beside the options, a scroll from the chips. */
  checkingFor: (allergens: string[]): string =>
    `Checking for ${allergens.join(", ")}`,
  bandContains: "Contains",
  /** "Groups" is an internal term; the guest-facing word is option. */
  bandInOptions: "In options",
  unavoidableBecause: (allergen: string, group: string): string =>
    `${allergen} is in every option of ${group}, so it cannot be avoided.`,
  /** Defines the asterisk the chips carry. */
  someRemovable: "* can be made without",
  bandMayContain: "May contain",
  bandInEveryOption: "In every option",
  bandInSomeOptions: "In some options",
  bandUnreadable: "Couldn’t be read",
  /** Stated once when no band has anything, so the card is never just empty. */
  bandNoAllergens: "No allergens declared",
  bandUnreadableValue: (count: number): string =>
    count === 1 ? "1 choice can’t be read" : `${count} choices can’t be read`,
  bandUnreadableNote:
    "Check this dish with the kitchen before answering an allergy question.",

  groupUnreadableTitle: "This choice can’t be read",

  optionSuitable: "Suitable",
  optionCanBeRemoved: "Can be removed",
  /** Avoidable, but by picking differently inside this option, not by omission. */
  optionDependsOnChoice: "Depends on choice",
  optionNotSuitable: "Not suitable",
  optionMayContain: "May contain",
  /** On a group heading once a selection is active. */
  optionsSuitable: (suitable: number, total: number): string =>
    `${suitable} of ${total} suitable`,

  optionIn: (group: string): string => `An option in ${group}`,

  backTo: (name: string): string => `Back to ${name}`,
  /** Only reachable if the dish we stepped in from lost its record mid-session. */
  backFallback: "the dish",
} as const;
